import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { UserCircle2, Menu, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useState } from 'react';

export default function Header() {
  const location = useLocation();
  const { user, loading, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const isActive = (path) => location.pathname === path;
  const isAdmin = user?.role === 'admin';
  
  const closeMobileMenu = () => setMobileMenuOpen(false);
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link" onClick={closeMobileMenu}>
            <div className={`bg-gradient-to-r from-blue-600 to-teal-600 text-white font-bold px-3 py-1.5 text-lg ${isAdmin ? 'animate-pulse' : ''}`}>
              Symetrix
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              data-testid="nav-home"
              className={`text-sm font-medium transition-colors ${
                isActive('/') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              data-testid="nav-about"
              className={`text-sm font-medium transition-colors ${
                isActive('/about') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              About
            </Link>
            <Link
              to="/services"
              data-testid="nav-services"
              className={`text-sm font-medium transition-colors ${
                isActive('/services') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Services
            </Link>
            <Link
              to="/careers"
              data-testid="nav-careers"
              className={`text-sm font-medium transition-colors ${
                isActive('/careers') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Careers
            </Link>
            <Link
              to="/blog"
              data-testid="nav-blog"
              className={`text-sm font-medium transition-colors ${
                isActive('/blog') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              data-testid="nav-contact"
              className={`text-sm font-medium transition-colors ${
                isActive('/contact') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Contact
            </Link>
          </nav>
          
          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {!loading && !user && (
              <>
                <Link
                  to="/login"
                  data-testid="login-link"
                  className="text-sm font-medium text-gray-700 hover:text-green-600 transition-colors"
                >
                  Login
                </Link>
                <Link to="/signup" data-testid="signup-button">
                  <Button className="bg-green-600 hover:bg-green-700 text-white">
                    Get Started
                  </Button>
                </Link>
              </>
            )}

            {!loading && user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="account-link"
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors"
                    aria-label={user.company || user.full_name || user.email || 'Account'}
                  >
                    <UserCircle2 size={26} />
                    <span>{user.role === 'admin' ? 'Symetrix Consulting' : (user.company || 'Company')}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/profile">My Profile</Link>
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard">Dashboard</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={async () => {
                      await logout();
                      window.location.href = '/';
                    }}
                  >
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="md:hidden p-2 text-gray-700 hover:text-blue-600 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <nav className="px-4 py-4 space-y-3">
            <Link
              to="/"
              onClick={closeMobileMenu}
              className={`block py-2 text-base font-medium transition-colors ${
                isActive('/') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              onClick={closeMobileMenu}
              className={`block py-2 text-base font-medium transition-colors ${
                isActive('/about') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              About
            </Link>
            <Link
              to="/services"
              onClick={closeMobileMenu}
              className={`block py-2 text-base font-medium transition-colors ${
                isActive('/services') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Services
            </Link>
            <Link
              to="/careers"
              onClick={closeMobileMenu}
              className={`block py-2 text-base font-medium transition-colors ${
                isActive('/careers') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Careers
            </Link>
            <Link
              to="/blog"
              onClick={closeMobileMenu}
              className={`block py-2 text-base font-medium transition-colors ${
                isActive('/blog') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              onClick={closeMobileMenu}
              className={`block py-2 text-base font-medium transition-colors ${
                isActive('/contact') ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Contact
            </Link>

            {/* Mobile Auth Actions */}
            <div className="pt-4 border-t border-gray-200 space-y-3">
              {!loading && !user && (
                <>
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="block py-2 text-base font-medium text-gray-700 hover:text-green-600 transition-colors"
                  >
                    Login
                  </Link>
                  <Link to="/signup" onClick={closeMobileMenu}>
                    <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}

              {!loading && user && (
                <>
                  <div className="py-2 text-sm font-medium text-gray-900">
                    {user.role === 'admin' ? 'Symetrix Consulting' : (user.company || 'Company')}
                  </div>
                  <Link
                    to="/profile"
                    onClick={closeMobileMenu}
                    className="block py-2 text-base font-medium text-gray-700 hover:text-blue-600 transition-colors"
                  >
                    My Profile
                  </Link>
                  {user.role === 'admin' && (
                    <Link
                      to="/dashboard"
                      onClick={closeMobileMenu}
                      className="block py-2 text-base font-medium text-gray-700 hover:text-blue-600 transition-colors"
                    >
                      Dashboard
                    </Link>
                  )}
                  <button
                    onClick={async () => {
                      await logout();
                      closeMobileMenu();
                      window.location.href = '/';
                    }}
                    className="w-full text-left py-2 text-base font-medium text-red-600 hover:text-red-700 transition-colors"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
